import { useEffect, useState } from 'react';
import { Box, Typography, InputBase, Avatar, CircularProgress, Button } from '@mui/material';
import {
  Search as SearchIcon,
  FolderOutlined,
  ChevronRight,
  HelpOutline,
  ReportProblemOutlined,
  PolicyOutlined,
  Add,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import DesignSystem from '../../designSystem';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import {
  fetchUserFolders,
  fetchUpNext,
  fetchUserCategories,
  fetchFavorites,
  fetchUserBusinesses,
  folderBusinesses,
  getBusinessLogoUrl,
  getBusinessCoverUrl,
  ApiFolder,
  ApiBusiness,
} from '../../services/api';
import { getApiLanguage } from '../../config/apiLanguage';
import { useT } from '../../i18n/useT';
import { displayFolderName } from '../../utils/folderDisplayName';
import { preferNonTestEntities, isTestEntityName } from '../../utils/testEntityName';

export const DESKTOP_COMPANION_WIDTH = 320;

/** Stable desc by platform save_count; missing counts sort last (fallback keeps prior order). */
function rankBySaveCount(items: ApiBusiness[]): ApiBusiness[] {
  return items
    .map((b, index) => ({ b, index }))
    .sort((a, c) => {
      const aCount = a.b.save_count;
      const cCount = c.b.save_count;
      const aMissing = aCount == null;
      const cMissing = cCount == null;
      if (aMissing !== cMissing) return aMissing ? 1 : -1;
      if (!aMissing && !cMissing && aCount !== cCount) return cCount - aCount;
      return a.index - c.index;
    })
    .map(({ b }) => b);
}

function coverOf(b: ApiBusiness): string | null {
  if (b.coverStrings) {
    const first = b.coverStrings.split(',').map((s) => s.trim()).filter(Boolean)[0];
    if (first) return getBusinessCoverUrl(first);
  }
  if (b.covers?.[0]?.cover) return getBusinessCoverUrl(b.covers[0].cover);
  return getBusinessLogoUrl(b.logo);
}

type CompanionMode = 'default' | 'profile' | 'settings';

function companionMode(pathname: string): CompanionMode {
  if (pathname.startsWith('/profile') || pathname.startsWith('/me')) return 'profile';
  if (pathname.startsWith('/settings')) return 'settings';
  return 'default';
}

function cardSx() {
  return {
    borderRadius: DesignSystem.desktop.companionCardRadius,
    backgroundColor: DesignSystem.colors.surface,
    border: `1px solid ${DesignSystem.colors.separator}`,
    overflow: 'hidden' as const,
  };
}

function cardTitleSx() {
  return {
    px: 2.25,
    pt: 2.1,
    pb: 1.1,
    fontSize: 17,
    fontWeight: 700,
    letterSpacing: '-0.025em',
    color: DesignSystem.colors.textPrimary,
  };
}

export default function CompanionPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const t = useT();
  const { mode: colorMode } = useThemeMode();
  void colorMode; // re-render companion when theme flips
  const [query, setQuery] = useState('');
  const [folders, setFolders] = useState<ApiFolder[]>([]);
  const [upNext, setUpNext] = useState<ApiBusiness[]>([]);
  const [visiStats, setVisiStats] = useState({ total: 0, visible: 0, real: 0 });
  const [loading, setLoading] = useState(false);
  const mode = companionMode(location.pathname);
  const hideSearch =
    location.pathname.startsWith('/search') || mode === 'settings' || mode === 'profile';

  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;
    setLoading(true);
    const lang = getApiLanguage();
    Promise.all([
      fetchUserFolders(user.token, lang).catch(() => ({ folders: [] as ApiFolder[] })),
      fetchUpNext(user.token, lang, 40).catch(() => ({ businesses: [] as ApiBusiness[] })),
      fetchUserCategories(lang, user.token, '0', '0', 3).catch(() => ({
        businesses: [] as ApiBusiness[],
        categories: [] as Array<{ id: number }>,
        subcategories: [] as Array<{ id: number }>,
      })),
      fetchFavorites(user.token, undefined, undefined, lang, user.id).catch(() => ({
        businesses: [] as ApiBusiness[],
      })),
      mode === 'profile'
        ? fetchUserBusinesses(user.token, lang).catch(() => ({ businesses: [] as ApiBusiness[] }))
        : Promise.resolve({ businesses: [] as ApiBusiness[] }),
    ])
      .then(([folderRes, upNextRes, catRes, favRes, myBizRes]) => {
        if (cancelled) return;
        setFolders((folderRes?.folders || []).slice(0, 4));

        let pool = [...(upNextRes?.businesses || [])];
        if (pool.length === 0) {
          const catBusinesses = (catRes as { businesses?: ApiBusiness[] })?.businesses || [];
          const categoryIds = new Set(
            ((catRes as { categories?: Array<{ id: number }> })?.categories || []).map((c) => c.id)
          );
          const subcategoryIds = new Set(
            ((catRes as { subcategories?: Array<{ id: number }> })?.subcategories || []).map((s) => s.id)
          );
          const categoryBusinessIds = new Set(catBusinesses.map((b) => b.id));
          const inSelected = (b: ApiBusiness) =>
            categoryBusinessIds.has(b.id) ||
            (b.category?.id != null && categoryIds.has(b.category.id)) ||
            (b.subcategory?.id != null && subcategoryIds.has(b.subcategory.id));

          const fromFolders = (folderRes?.folders || []).flatMap((f) => folderBusinesses(f));
          const fromFavs = favRes?.businesses || [];
          const fromFeedSaved = catBusinesses.filter((b) => (b.favorite ?? 0) === 1);
          const counts = new Map<number, number>();
          const byId = new Map<number, ApiBusiness>();
          for (const b of [...fromFavs, ...fromFolders, ...fromFeedSaved]) {
            if (!inSelected(b)) continue;
            counts.set(b.id, (counts.get(b.id) || 0) + 1);
            byId.set(b.id, b);
          }
          // Local save frequency when /upNext is empty; do not shuffle.
          pool = Array.from(byId.values()).sort(
            (a, b) => (counts.get(b.id) || 0) - (counts.get(a.id) || 0)
          );
        }

        // Server already ranks by platform save_count; re-sort so UI stays ranked.
        setUpNext(preferNonTestEntities(rankBySaveCount(pool), 5));

        const mine = myBizRes?.businesses || [];
        const real = mine.filter((b) => !isTestEntityName(b.name));
        const visible = mine.filter((b) => (b.active ?? 1) === 1).length;
        setVisiStats({ total: mine.length, visible, real: real.length });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.token, user?.id, mode]);

  const submitSearch = () => {
    const q = query.trim();
    if (!q) {
      navigate('/search');
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const listsBlock = (
    <Box sx={cardSx()}>
      <Typography sx={cardTitleSx()}>{t('companionLists')}</Typography>
      {folders.length === 0 ? (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mb: 1 }}>
            {t('companionListsEmpty')}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/bookmarks')}
            sx={{
              border: 0,
              background: 'transparent',
              color: DesignSystem.colors.primary,
              font: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              p: 0,
            }}
          >
            {t('companionOpenLists')}
          </Box>
        </Box>
      ) : (
        folders.map((folder, i) => (
          <Box
            key={folder.id}
            component="button"
            type="button"
            onClick={() => navigate(`/bookmarks/${folder.id}`)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              width: '100%',
              border: 0,
              background: 'transparent',
              px: 2,
              py: 1.1,
              cursor: 'pointer',
              textAlign: 'left',
              font: 'inherit',
              '&:hover': { backgroundColor: DesignSystem.colors.hover },
            }}
          >
            <Typography
              sx={{ width: 18, fontSize: 13, color: DesignSystem.colors.textTertiary, fontWeight: 600 }}
            >
              {i + 1}
            </Typography>
            <Avatar
              variant="rounded"
              sx={{
                width: 32,
                height: 32,
                bgcolor: DesignSystem.colors.surfaceElevated,
                color: DesignSystem.colors.textSecondary,
              }}
            >
              <FolderOutlined sx={{ fontSize: 16 }} />
            </Avatar>
            <Typography
              sx={{
                flex: 1,
                fontSize: 14,
                fontWeight: 600,
                color: DesignSystem.colors.textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayFolderName(folder.name, i)}
            </Typography>
            <ChevronRight sx={{ fontSize: 18, color: DesignSystem.colors.textTertiary }} />
          </Box>
        ))
      )}
    </Box>
  );

  const upNextBlock = (
    <Box sx={cardSx()}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
        <Typography sx={cardTitleSx()}>{t('companionUpNext')}</Typography>
        <Box
          component="button"
          type="button"
          onClick={() => navigate('/search')}
          sx={{
            border: 0,
            background: 'transparent',
            color: DesignSystem.colors.textSecondary,
            font: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            mt: 1.5,
            '&:hover': { color: DesignSystem.colors.primary },
          }}
        >
          {t('companionSeeAll')}
        </Box>
      </Box>
      {loading && upNext.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} sx={{ color: DesignSystem.colors.primary }} />
        </Box>
      ) : upNext.length === 0 ? (
        <Typography sx={{ px: 2, pb: 2, fontSize: 13, color: DesignSystem.colors.textSecondary }}>
          {t('companionUpNextEmpty')}
        </Typography>
      ) : (
        upNext.map((b) => {
          const handle = (b.username || String(b.id)).replace(/^@/, '');
          const thumb = coverOf(b);
          return (
            <Box
              key={b.id}
              component="button"
              type="button"
              onClick={() => navigate(`/${handle}`)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                width: '100%',
                border: 0,
                background: 'transparent',
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
                '&:hover': { backgroundColor: DesignSystem.colors.hover },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: DesignSystem.colors.textTertiary,
                    mb: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {b.category?.name || b.subcategory?.name || `@${handle}`}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: DesignSystem.colors.textPrimary,
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {b.name}
                </Typography>
              </Box>
              {thumb && (
                <Box
                  component="img"
                  src={thumb}
                  alt=""
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '8px',
                    objectFit: 'cover',
                    flexShrink: 0,
                    bgcolor: DesignSystem.colors.surfaceElevated,
                  }}
                />
              )}
            </Box>
          );
        })
      )}
    </Box>
  );

  const profileBlock = (
    <Box sx={cardSx()}>
      <Typography sx={cardTitleSx()}>{t('companionVisis')}</Typography>
      <Box sx={{ px: 2.25, pb: 2 }}>
        <Typography sx={{ fontSize: 14, color: DesignSystem.colors.textSecondary, mb: 1.25 }}>
          {visiStats.total === 0
            ? t('companionNoVisis')
            : t('companionVisiStats', { total: visiStats.total, visible: visiStats.visible })}
        </Typography>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/visi/create')}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: DesignSystem.borderRadius.pill,
            bgcolor: DesignSystem.colors.action,
            color: DesignSystem.colors.onPrimary,
            boxShadow: 'none',
            '&:hover': { bgcolor: DesignSystem.colors.actionDark, boxShadow: 'none' },
          }}
        >
          {t('companionAddNew')}
        </Button>
      </Box>
    </Box>
  );

  const backendUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const settingsHelpBlock = (
    <Box sx={cardSx()}>
      <Typography sx={cardTitleSx()}>{t('companionHelpLegal')}</Typography>
      {[
        {
          label: t('companionHelpCenter'),
          icon: <HelpOutline sx={{ fontSize: 18 }} />,
          onClick: () => window.open('https://visime.com/support', '_blank', 'noopener,noreferrer'),
        },
        {
          label: t('companionReport'),
          icon: <ReportProblemOutlined sx={{ fontSize: 18 }} />,
          onClick: () => navigate('/settings/report'),
        },
        {
          label: t('companionPrivacy'),
          icon: <PolicyOutlined sx={{ fontSize: 18 }} />,
          onClick: () => navigate('/settings/privacy'),
        },
      ].map((item) => (
        <Box
          key={item.label}
          component="button"
          type="button"
          onClick={item.onClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            width: '100%',
            border: 0,
            background: 'transparent',
            px: 2,
            py: 1.25,
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
            color: DesignSystem.colors.textPrimary,
            '&:hover': { backgroundColor: DesignSystem.colors.hover },
          }}
        >
          <Box sx={{ color: DesignSystem.colors.primary, display: 'flex' }}>{item.icon}</Box>
          <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{item.label}</Typography>
          <ChevronRight sx={{ fontSize: 18, color: DesignSystem.colors.textTertiary }} />
        </Box>
      ))}
    </Box>
  );

  const accountChip =
    user && mode === 'settings' ? (
      <Box
        sx={{
          ...cardSx(),
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.5,
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: DesignSystem.colors.surfaceElevated,
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          {(user.email || user.firstName || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {(() => {
            const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
            const email = (user.email || '').trim();
            const primary = name || email || t('navAccount');
            const secondary = name && email && name !== email ? email : '';
            return (
              <>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: DesignSystem.colors.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {primary}
                </Typography>
                {secondary ? (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: DesignSystem.colors.textSecondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {secondary}
                  </Typography>
                ) : null}
              </>
            );
          })()}
        </Box>
      </Box>
    ) : null;

  return (
    <Box
      component="aside"
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: DESKTOP_COMPANION_WIDTH,
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        gap: 2.75,
        px: 2,
        py: 2.75,
        zIndex: 1100,
        backgroundColor: DesignSystem.colors.canvas,
        overflowY: 'auto',
      }}
    >
      {!hideSearch && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 1.2,
            borderRadius: DesignSystem.desktop.searchRadius,
            backgroundColor: DesignSystem.colors.surface,
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: DesignSystem.colors.textSecondary }} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitSearch();
            }}
            placeholder={t('companionSearch')}
            sx={{
              flex: 1,
              fontSize: 14,
              color: DesignSystem.colors.textPrimary,
              '& input::placeholder': { color: DesignSystem.colors.textSecondary, opacity: 1 },
            }}
          />
        </Box>
      )}

      {mode === 'settings' && (
        <>
          {accountChip}
          {settingsHelpBlock}
        </>
      )}

      {mode === 'profile' && (
        <>
          {profileBlock}
          {listsBlock}
        </>
      )}

      {mode === 'default' && (
        <>
          {upNextBlock}
          {listsBlock}
        </>
      )}
    </Box>
  );
}
